import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navbar } from "../components/Layout/Navbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useCookies } from "react-cookie";
import Footer from "../components/Auth/Footer";
import Button from "@mui/material/Button";
import ColorPalletsList from "../components/Admin/ColorPalletsList";
import TemplateCategoryList from "../components/Admin/TemplateCategoryList";
import PublishableTemplatesList from "../components/Admin/PublishableTemplatesList";
import UserAcquisitionLogs from "../components/Admin/UserAcquisitionLogs";
import SitesList from "../components/Admin/SitesList";
import AITemplates from "../components/Admin/AITemplates";
import axiosInstance from "../axiosInstance";
import { enqueueSnackbar } from "notistack";

const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [cookies] = useCookies(["rp_user_data"]);
  const location = useLocation();
  const stateData = location.state;

  // Marketing site id (fixed)
  const marketingSiteId = "2e2b85c5-1b76-4e74-8e39-c82749386c53";

  // Publish status for smart button
  const [publishInfo, setPublishInfo] = useState({
    status: "Unknown",
    stuck: false,
    publishBatchStart: 0,
  });

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const l_menu = query.get("menu");

  // Polling interval ref so we can stop polling when publish is terminal
  const pollRef = useRef<number | null>(null);

  const fetchPublishInfo = async () => {
    try {
      const resp = await axiosInstance.get("/api/configs/check-publish-status", {
        params: { siteId: marketingSiteId },
      });

      setPublishInfo({
        status: resp?.data?.status ?? "Unknown",
        stuck: !!resp?.data?.stuck,
        publishBatchStart: Number(resp?.data?.publishBatchStart || 0),
      });
    } catch (e) {
      // Best effort. Don't spam errors in admin UI.
      // Keep previous publishInfo so button doesn't flicker.
    }
  };

  useEffect(() => {
    // Only poll when admin home is shown (where the button exists)
    if (l_menu !== null) return;

    fetchPublishInfo();

    // Start polling
    pollRef.current = window.setInterval(fetchPublishInfo, 15000);

    return () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [l_menu]);


  useEffect(() => {
    // Stop polling once publish reaches a terminal state
    const terminal =
      publishInfo.status === "Completed" || publishInfo.status === "Failed";

    if (terminal && pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [publishInfo.status]);

  const handleMarketingSiteRepublish = async () => {
    try {
      // Refresh current status just before action
      const statusRes = await axiosInstance.get("/api/configs/check-publish-status", {
        params: { siteId: marketingSiteId },
      });

      const { status, stuck, publishBatchStart } = statusRes.data || {};

      // If stuck -> resume
      if (status === "Running" && stuck) {
        await axiosInstance.post("/api/configs/publish/resume", {
          siteId: marketingSiteId,
        });

        enqueueSnackbar(
          `Publish was stuck. Resuming from batch ${Number(publishBatchStart || 0)}…`,
          { variant: "warning" }
        );

        fetchPublishInfo();
        return;
      }

      // If running (fresh) -> do nothing
      if (status === "Running") {
        enqueueSnackbar("Publish is already running…", { variant: "info" });
        return;
      }

      // Otherwise -> start publish
      const res = await axiosInstance.post("/api/configs/publish", {
        siteId: marketingSiteId,
        publishAllPages: true,
      });

      if (res.status === 200) {
        enqueueSnackbar("Publishing in progress!", { variant: "warning" });
        fetchPublishInfo();
      } else {
        enqueueSnackbar("Could not start publish.", { variant: "error" });
      }
    } catch (error) {
      console.error("Marketing republish/resume error", error);
      enqueueSnackbar("Something went wrong while publishing.", { variant: "error" });
    }
  };

  const marketingButtonText =
    publishInfo.status === "Running" && publishInfo.stuck
      ? `Resume Marketing Publish (batch ${publishInfo.publishBatchStart})`
      : publishInfo.status === "Running"
      ? "Publishing…"
      : "Republish Marketing Site";

  const marketingButtonDisabled =
    publishInfo.status === "Running" && !publishInfo.stuck;

  return (
    <>
      <Helmet>
        <title>RocketPages - Admin Panel</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="font-body text-base font-medium">
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Navbar
          {...stateData}
          title={`Admin Panel ${
            l_menu === "templateCategory"
              ? " | Template Category"
              : l_menu === "colorPallets"
              ? " | Color Pallets"
              : l_menu === "publishTemplates"
              ? " | Publish Templates"
              : l_menu === "userLogs"
              ? " | User acquisition logs"
              : l_menu === "allSites"
              ? " | Sites List"
              : l_menu === "aiTemplates"
              ? " | AI Templates"
              : ""
          }`}
        />

        <div className="font-body text-base font-medium bg-gray-100 w-full pt-[64px] flex flex-col min-h-screen-minus-100">
          {l_menu === "colorPallets" && <ColorPalletsList />}
          {l_menu === "templateCategory" && <TemplateCategoryList />}
          {l_menu === "publishTemplates" && <PublishableTemplatesList />}
          {l_menu === "userLogs" && <UserAcquisitionLogs />}
          {l_menu === "allSites" && <SitesList />}
          {l_menu === "aiTemplates" && <AITemplates />}

          {l_menu === null && (
            <div className="flex flex-row m-4">
              <Button
                id="save"
                className="min-w-[6rem] px-4 py-1 capitalize mr-2"
                sx={{ textTransform: "none" }}
                size="small"
                variant="contained"
                onClick={() => navigate(`/admin?menu=colorPallets`)}
              >
                Color Pallets
              </Button>

              <Button
                id="save"
                className="min-w-[6rem] px-4 py-1 capitalize mr-2"
                sx={{ textTransform: "none" }}
                size="small"
                variant="contained"
                onClick={() => navigate(`/admin?menu=templateCategory`)}
              >
                Template Category
              </Button>

              <Button
                id="save"
                className="min-w-[6rem] px-4 py-1 capitalize mr-2"
                sx={{ textTransform: "none" }}
                size="small"
                variant="contained"
                onClick={() => navigate(`/admin?menu=publishTemplates`)}
              >
                Publish Templates
              </Button>

              <Button
                id="save"
                className="min-w-[6rem] px-4 py-1 capitalize mr-2"
                sx={{ textTransform: "none" }}
                size="small"
                variant="contained"
                onClick={() => navigate(`/admin?menu=userLogs`)}
              >
                User acquisition logs
              </Button>

              <Button
                id="save"
                className="min-w-[6rem] px-4 py-1 capitalize mr-2"
                sx={{ textTransform: "none" }}
                size="small"
                variant="contained"
                onClick={() => navigate(`/admin?menu=allSites`)}
              >
                Sites List
              </Button>

              <Button
                id="save"
                className="min-w-[6rem] px-4 py-1 capitalize mr-2"
                sx={{ textTransform: "none" }}
                size="small"
                variant="contained"
                onClick={() => navigate(`/admin?menu=aiTemplates`)}
              >
                AI Templates
              </Button>

              <Button
                id="save"
                className="min-w-[6rem] px-4 py-1 capitalize mr-2"
                sx={{ textTransform: "none" }}
                size="small"
                variant="contained"
                disabled={marketingButtonDisabled}
                onClick={handleMarketingSiteRepublish}
              >
                {marketingButtonText}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="w-full">
        <Footer />
      </div>
    </>
  );
};

export default AdminPage;